"use client"

import React, { Component, ErrorInfo, ReactNode } from "react"
import { ErrorDisplay } from "./error-display"
import { getUserFriendlyError } from "@/lib/errors/error-messages"

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component for catching React errors
 * Wraps components to catch and display errors gracefully
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    // Skip benign ResizeObserver errors that Safari sometimes throws
    const message = error?.message || ""
    if (
      message.includes("ResizeObserver") || 
      message.includes("loop completed with undelivered notifications") ||
      message.includes("Illegal constructor")
    ) {
      return { hasError: false, error: null }
    }
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skip benign ResizeObserver errors
    const message = error?.message || ""
    if (
      message.includes("ResizeObserver") || 
      message.includes("loop completed with undelivered notifications") ||
      message.includes("Illegal constructor")
    ) {
      return
    }

    // Get user-friendly error message
    const { message: userMessage } = getUserFriendlyError(error)

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", {
        error,
        userMessage,
        errorInfo,
        componentStack: errorInfo.componentStack,
      })
    }

    // In production, you might want to send to error tracking service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } })

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default error display
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Get user-friendly error message
      const { message: userMessage } = getUserFriendlyError(this.state.error)

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 p-4">
          <div className="max-w-md w-full">
            <ErrorDisplay
              error={userMessage}
              title="Application Error"
              description="Something unexpected happened. Please try refreshing the page."
              onRetry={this.handleReset}
              retryLabel="Try Again"
            />
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

